import { ReactNode } from 'react';
import { AutoComplete, AutoCompleteProps } from 'antd';
import {
  Controller,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import * as React from 'react';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';

export interface FormAutoCompleteOption<Value = any> {
  label: ReactNode;
  value?: Value;
}

export interface FormAutoCompleteProps<
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FieldPathValue<FormData, Name> = FieldPathValue<FormData, Name>,
> extends Omit<AutoCompleteProps<Value>, 'id'> {
  id?: string;
  name: Name;
  options?: FormAutoCompleteOption<Value>[];
}

export const FormAutoComplete = <
  FormData extends FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
  Value extends FormData[Name] = FormData[Name],
>(
  props: FormAutoCompleteProps<FormData, Name, Value>,
) => {
  const getIdFromName = useGetIdFromName();
  const { name, id = getIdFromName(name), ...rest } = props;
  const { control } = useFormContext();
  const { onChange: formOnChange } = useAdditionalFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { value, disabled } = field;
        const onChange = (
          value: Value,
          option?:
            | FormAutoCompleteOption<Value>
            | FormAutoCompleteOption<Value>[],
        ) => {
          rest?.onChange?.(value, option);
          field.onChange(value);
          formOnChange?.();
        };
        return (
          <AutoComplete
            disabled={disabled}
            {...rest}
            id={id}
            value={value}
            onChange={onChange}
          />
        );
      }}
    />
  );
};
